import axios from 'axios';
import { FEATURES } from '@/lib/features';
import axiosInstance from './axios';

/*
 * Customer sign-in with a one-time code, by mobile (code on WhatsApp) or email (code by email).
 *
 * API (public, base path /api/auth):
 *   POST /sendCustomerLoginOTP         { mobile_number }       → { data: { expires_in, resend_in } }
 *   POST /verifyCustomerLoginOTP       { mobile_number, otp }  → { token, user }  (same shape as /loginCustomer)
 *   POST /sendCustomerLoginEmailOTP    { email }               → same as mobile   ┐ requested from the backend
 *   POST /verifyCustomerLoginEmailOTP  { email, otp }          → same as mobile   ┘ (see backend-requests.md)
 * Errors are { success: false, message } with user-facing messages:
 *   400 invalid mobile/email / bad, wrong, expired or used OTP · 404 no account
 *   429 resend too soon, >5 OTPs an hour, or 5 wrong attempts · 502 delivery failed
 *
 * The real API is the default. Only VITE_OTP_AUTH_MOCK='true' switches to the in-browser mock. Mock test data:
 *   - any valid number or email          → OTP sent; the correct code is 482916
 *   - 91234 56789 / noplans@example.com  → "No purchases found"
 *   - code 000000                        → "This code has expired"
 *   - 3 wrong codes                      → "Too many attempts" (locked for 30 minutes)
 */

/** Who is signing in: a 10-digit Indian mobile, or an email address. */
export type LoginContact = { channel: 'mobile'; mobile: string } | { channel: 'email'; email: string };

const contactKey = (c: LoginContact) => (c.channel === 'mobile' ? c.mobile : c.email.toLowerCase());

export interface OtpUser {
  name: string | null;
  email: string;
  phone: string | null;
  address: unknown;
}

export interface BrandSummary {
  name: string;
  summary: string;
}

export interface OtpCustomer {
  user: OtpUser;
  /** Only the mock provides these; the API does not return purchases on sign-in. */
  productCount?: number;
  brands?: BrandSummary[];
}

export type RequestOtpResult =
  | { status: 'sent'; resendInSeconds: number; expiresInSeconds: number }
  | { status: 'not_linked' }
  /** Asked again before the resend timer ended. */
  | { status: 'cooldown'; seconds: number; message: string }
  /** Too many codes requested; sign-in is paused. */
  | { status: 'locked'; message: string }
  /** Delivery failed or the number was rejected; safe to retry. */
  | { status: 'failed'; message: string };

export type VerifyOtpResult =
  | { status: 'verified'; token: string; customer: OtpCustomer }
  | { status: 'invalid'; message: string }
  /** The code can no longer be used (expired, used, or too many wrong tries); a new one is needed. */
  | { status: 'expired'; reason: 'expired' | 'too_many_attempts'; title: string; message: string }
  | { status: 'locked'; message: string }
  | { status: 'failed'; message: string };

const isMockMode = () => import.meta.env.VITE_OTP_AUTH_MOCK === 'true';

const GENERIC_ERROR = 'Something went wrong. Please try again.';

// ---------- API ----------

interface ApiError {
  status: number;
  message: string;
}

const UNREACHABLE_ERROR = 'We couldn’t reach Protega right now. Please try again in a moment.';
const EMAIL_NOT_LIVE = 'Signing in with email isn’t available yet. Please use your mobile number for now.';
/** toApiError status for "the server has no such route", e.g. email sign-in before the backend adds it. */
const NO_ROUTE = -404;

const toApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as { message?: unknown } | undefined;
    // Only trust the status when the API itself answered (JSON with a message). A proxy or
    // tunnel error page — e.g. ngrok's 404 when it's offline — must not read as "no account".
    if (body && typeof body === 'object' && typeof body.message === 'string') {
      return { status: err.response!.status, message: body.message };
    }
    if (err.response?.status === 404 && /cannot (post|get)/i.test(String(err.response.data))) return { status: NO_ROUTE, message: '' };
    return { status: 0, message: UNREACHABLE_ERROR };
  }
  return { status: 0, message: GENERIC_ERROR };
};

const ENDPOINTS = {
  mobile: { send: '/api/auth/sendCustomerLoginOTP', verify: '/api/auth/verifyCustomerLoginOTP' },
  email: { send: '/api/auth/sendCustomerLoginEmailOTP', verify: '/api/auth/verifyCustomerLoginEmailOTP' },
};

const contactBody = (c: LoginContact) => (c.channel === 'mobile' ? { mobile_number: c.mobile } : { email: c.email });

async function apiRequestOtp(contact: LoginContact): Promise<RequestOtpResult> {
  if (contact.channel === 'email' && !FEATURES.emailOtp) return { status: 'failed', message: EMAIL_NOT_LIVE };
  try {
    const { data } = await axiosInstance.post<{ data: { resend_in: number; expires_in: number } }>(
      ENDPOINTS[contact.channel].send,
      contactBody(contact),
    );
    return { status: 'sent', resendInSeconds: data.data.resend_in, expiresInSeconds: data.data.expires_in };
  } catch (err) {
    const { status, message } = toApiError(err);
    if (status === NO_ROUTE) return { status: 'failed', message: EMAIL_NOT_LIVE };
    if (status === 404) return { status: 'not_linked' };
    if (status === 429) {
      // "Please wait Ns…" means the resend timer hasn't run out; anything else is the hourly limit.
      const wait = message.match(/wait\s+(\d+)\s*s/i);
      return wait ? { status: 'cooldown', seconds: Number(wait[1]), message } : { status: 'locked', message };
    }
    return { status: 'failed', message };
  }
}

async function apiVerifyOtp(contact: LoginContact, otp: string): Promise<VerifyOtpResult> {
  if (contact.channel === 'email' && !FEATURES.emailOtp) return { status: 'failed', message: EMAIL_NOT_LIVE };
  try {
    const { data } = await axiosInstance.post<{ token: string; user: OtpUser }>(ENDPOINTS[contact.channel].verify, {
      ...contactBody(contact),
      otp,
    });
    return { status: 'verified', token: data.token, customer: { user: data.user } };
  } catch (err) {
    const { status, message } = toApiError(err);
    if (status === NO_ROUTE) return { status: 'failed', message: EMAIL_NOT_LIVE };
    if (status === 429) return { status: 'expired', reason: 'too_many_attempts', title: 'Too many incorrect attempts', message };
    if (status === 400 && /expired|already used/i.test(message)) {
      return { status: 'expired', reason: 'expired', title: 'This code has expired', message };
    }
    if (status === 400) return { status: 'invalid', message };
    return { status: 'failed', message };
  }
}

// ---------- Mock ----------

const MOCK = {
  code: '482916',
  expiredCode: '000000',
  unlinkedNumber: '9123456789',
  unlinkedEmail: 'noplans@example.com',
  maxAttempts: 3,
  lockMinutes: 30,
  resendSeconds: 60,
  expiresSeconds: 300,
  customer: {
    user: { name: 'Priya', email: 'priya@example.com', phone: null, address: null },
    productCount: 4,
    brands: [{ name: 'EUME', summary: '4 products · 2 protected, 1 on the way' }],
  } satisfies OtpCustomer,
};

const mockSessions = new Map<string, { attemptsLeft: number; lockedUntil?: number }>();

const delay = (ms = 600) => new Promise((resolve) => setTimeout(resolve, ms));

const describe = (c: LoginContact) => (c.channel === 'mobile' ? `+91 ${c.mobile.slice(0, 5)} ${c.mobile.slice(5)}` : c.email);

const lockedResult = (contact: LoginContact) => {
  const lockedUntil = mockSessions.get(contactKey(contact))?.lockedUntil;
  if (!lockedUntil || lockedUntil <= Date.now()) return null;
  const minutes = Math.ceil((lockedUntil - Date.now()) / 60_000);
  const time = new Date(lockedUntil).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return {
    status: 'locked',
    message: `For your security, sign-in for ${describe(contact)} is paused for ${minutes} minutes. You can try again at ${time}.`,
  } as const;
};

async function mockRequestOtp(contact: LoginContact): Promise<RequestOtpResult> {
  await delay();
  const key = contactKey(contact);
  const locked = lockedResult(contact);
  if (locked) return locked;
  if (key === MOCK.unlinkedNumber || key === MOCK.unlinkedEmail) return { status: 'not_linked' };
  mockSessions.set(key, { attemptsLeft: MOCK.maxAttempts });
  return { status: 'sent', resendInSeconds: MOCK.resendSeconds, expiresInSeconds: MOCK.expiresSeconds };
}

async function mockVerifyOtp(contact: LoginContact, otp: string): Promise<VerifyOtpResult> {
  await delay();
  const key = contactKey(contact);
  const locked = lockedResult(contact);
  if (locked) return locked;
  if (otp === MOCK.expiredCode) {
    return { status: 'expired', reason: 'expired', title: 'This code has expired', message: 'Codes stay valid for 5 minutes. Request a new one to continue.' };
  }
  if (otp === MOCK.code) {
    mockSessions.delete(key);
    return { status: 'verified', token: '', customer: MOCK.customer };
  }
  const session = mockSessions.get(key) ?? { attemptsLeft: MOCK.maxAttempts };
  session.attemptsLeft -= 1;
  mockSessions.set(key, session);
  if (session.attemptsLeft <= 0) {
    session.lockedUntil = Date.now() + MOCK.lockMinutes * 60_000;
    return lockedResult(contact)!;
  }
  const left = session.attemptsLeft;
  return { status: 'invalid', message: `Incorrect code. ${left} ${left === 1 ? 'attempt' : 'attempts'} left.` };
}

/** Sends (or re-sends) a 6-digit sign-in code: on WhatsApp for a mobile, by email for an email. Re-sending invalidates the previous one. */
export const requestLoginOtp = (contact: LoginContact) => (isMockMode() ? mockRequestOtp(contact) : apiRequestOtp(contact));

/** Checks the code. Pass the same contact that was used to send it. */
export const verifyLoginOtp = (contact: LoginContact, otp: string) => (isMockMode() ? mockVerifyOtp(contact, otp) : apiVerifyOtp(contact, otp));

// Shared with the plan-recovery flow, which uses the same error mapping and mock switch.
export { isMockMode, toApiError, NO_ROUTE };
